import * as ko from 'knockout';
import {_} from 'lodash';
import PromiseRequest from '../../../req/PromiseRequest';
import {
    IMessageData,
    IObjectData,
    IOrganization,
    IOverride,
    ISystem,
    ISystemPermission,
    ISystemRole,
} from '../../../index';
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SYSTEM_MEGA_EADM} from '../../../constants';
import {ImportFormatId} from '../../../constants/ImportFormats';
import {TSyncfield_Department} from '../../../constants/Syncfields';
import {REQUEST_MESSAGE} from '../../../constants/Language';
import {global, lm} from '../../../appContext';
import {
    createErrorMessage,
    getObjectDefinition,
    getObjectDisplayName,
    getObjectType,
    getPermissionDisplayName,
    getRawSyncFieldValue,
} from '../../../util/misc';
import {getSearchObjectParams, getSearchSystemParams} from '../../../util/request';
import {nsSyncFields} from '../../../req/nsSyncFields';
import {nsTooltip} from '../../../util/nsTooltip';
import {storeObservable} from '../../../util/sessionstore';
import {KOSubscriptionTracker} from '../../../util/KOSubscriptionTracker';
import {ConcurrentRequestQueue} from '../../../req/ConcurrentRequestQueue';
import {IComboboxV2Options} from '../../combobox/v2';
import {IComboboxOptions_Department, TObjectData_basicDepartment} from '../../combobox/department';
import {IPermissionRowActions, PermissionRow, permissionRowReconcileKey} from './PermissionRow';
import {nsUserPermissions} from './nsUserPermissions';
import {nsUserPermissionPolicy} from './nsUserPermissionPolicy';

/** Same syncfields as combobox/department — enough for display name + tooltips. Resolved once at page load. */
export const DEPARTMENT_SEARCH_COLS: TSyncfield_Department[] = [
    'Id',
    'ParentDepartmentNumber',
    ...(getObjectDefinition(ObjectTypeId.Department)?.syncfieldDisplayName as TSyncfield_Department[] ?? []),
];

/** One system with its permission rows, sorted for grid display. */
export class SystemPermissionGroup {
    readonly systemId: number;
    readonly systemName: string;
    /** Untranslated tooltip key — same source as the old permission view. */
    readonly systemTooltip: string;
    /** System details href when allowed; rendered only in page read mode. */
    readonly systemHref: string | null;
    /** `legalunit` or `user.permissions.column.department` from the group's managerOf types. */
    readonly scopeColumnKey: string;
    readonly permissions: PermissionRow[];
    /** Rows matching the page status filter (drives grid foreach + selection). */
    readonly filteredPermissions: KnockoutComputed<PermissionRow[]>;
    /** Header checkbox: checked iff every row is selected; write selects/deselects all. */
    readonly areAllSelected: KnockoutComputed<boolean>;
    /** Header checkbox indeterminate when some (but not all) rows are selected. */
    readonly areSomeSelected: KnockoutComputed<boolean>;
    /** True when at least one row still shows a selection checkbox. */
    readonly hasSelectablePermissions: KnockoutComputed<boolean>;

    constructor(params: {
        systemId: number;
        systemName: string;
        systemTooltip: string;
        systemHref?: string | null;
        permissions: PermissionRow[];
        statusFilter: KnockoutObservable<nsUserPermissions.TPermissionStatusFilter>;
    }) {
        this.systemId = params.systemId;
        this.systemName = params.systemName;
        this.systemTooltip = params.systemTooltip;
        this.systemHref = params.systemHref ?? null;
        this.permissions = params.permissions;
        this.scopeColumnKey = nsUserPermissions.scopeColumnHeaderKey(
            params.permissions.map(p => p.systemPermission),
        );

        this.filteredPermissions = ko.pureComputed(() => {
            const filter = params.statusFilter();
            return this.permissions.filter(p =>
                nsUserPermissions.permissionMatchesStatusFilter(p.toStatusFilterRow(), filter),
            );
        });
        this.hasSelectablePermissions = ko.pureComputed(() => this.selectablePermissions().length > 0);
        this.areAllSelected = ko.computed({
            read: () => {
                const selectable = this.selectablePermissions();
                return selectable.length > 0 && selectable.every(p => p.isSelected());
            },
            write: (checked: boolean) => {
                this.selectablePermissions().forEach(p => p.isSelected(checked));
            },
        });
        this.areSomeSelected = ko.pureComputed(() => {
            const selectable = this.selectablePermissions();
            const selectedCount = selectable.filter(p => p.isSelected()).length;
            return selectedCount > 0 && selectedCount < selectable.length;
        });
    }

    /** Visible (filtered) rows that still show a checkbox (removed / pending soft-delete do not). */
    private selectablePermissions(): PermissionRow[] {
        return this.filteredPermissions().filter(p => p.showSelectionCheckbox());
    }

    dispose(options?: {disposePermissions?: boolean}): void {
        this.filteredPermissions.dispose();
        this.hasSelectablePermissions.dispose();
        this.areAllSelected.dispose();
        this.areSomeSelected.dispose();
        if (options?.disposePermissions !== false) {
            this.permissions.forEach(p => p.dispose());
        }
    }
}

/** Shared empty list so disabled department pickers don't allocate a new `[]` every recompute. */
const EMPTY_DEPARTMENTS: TObjectData_basicDepartment[] = [];

/** One draft row in the New Permission grid (add-MP shell). */
class NewPermissionDraftRow {
    readonly selectedSystem: KnockoutObservable<ISystem | null> = ko.observable(null);
    readonly selectedRole: KnockoutObservable<ISystemRole | null> = ko.observable(null);
    readonly selectedDepartment: KnockoutObservable<TObjectData_basicDepartment | null> = ko.observable(null);
    readonly expirydate: KnockoutObservable<string> = ko.observable('');
    readonly needsDepartment: KnockoutComputed<boolean>;
    /** Truthy trimmed `description` from the selected system, if any. */
    readonly systemDescription: KnockoutComputed<string>;
    /** Truthy trimmed `description` from the selected role, if any. */
    readonly roleDescription: KnockoutComputed<string>;
    readonly systemComboboxParams: IComboboxV2Options<ISystem>;
    readonly roleComboboxParams: IComboboxV2Options<ISystemRole>;
    readonly departmentComboboxParams: IComboboxOptions_Department;

    private readonly kosub = new KOSubscriptionTracker();
    /** One occupancy snapshot per change, shared by all three pickers on this row. */
    private readonly occupancySlots: KnockoutComputed<nsUserPermissionPolicy.IOccupancySlot[]>;
    private readonly availableSystems: KnockoutComputed<ISystem[]>;
    private readonly availableRoles: KnockoutComputed<ISystemRole[]>;
    private readonly availableDepartments: KnockoutComputed<TObjectData_basicDepartment[]>;
    /** Last emitted picker lists — reused when ids are unchanged so combobox `data` does not notify. */
    private cachedSystems: ISystem[] = [];
    private cachedRoles: ISystemRole[] = [];
    private cachedDepartments: TObjectData_basicDepartment[] = EMPTY_DEPARTMENTS;

    constructor(
        getSystems: () => ISystem[],
        departments: KnockoutObservableArray<TObjectData_basicDepartment>,
        getOccupancy: () => nsUserPermissionPolicy.IOccupancySlot[],
        organization: IOrganization,
        canInteract: () => boolean,
    ) {
        this.occupancySlots = ko.pureComputed(() => getOccupancy());
        this.availableSystems = ko.pureComputed(() => {
            const next = nsUserPermissionPolicy.filterSystemsForPicker(
                getSystems(),
                this.occupancySlots(),
                organization,
                this.selectedSystem()?.id,
            );
            this.cachedSystems = nsUserPermissionPolicy.reuseArrayIfSameIds(this.cachedSystems, next);
            return this.cachedSystems;
        });
        this.availableRoles = ko.pureComputed(() => {
            const next = nsUserPermissionPolicy.rolesForPicker(
                this.selectedSystem(),
                this.occupancySlots(),
                this.selectedRole()?.id,
            );
            this.cachedRoles = nsUserPermissionPolicy.reuseArrayIfSameIds(this.cachedRoles, next);
            return this.cachedRoles;
        });
        this.availableDepartments = ko.pureComputed(() => {
            const system = this.selectedSystem();
            const role = this.selectedRole();
            // Field is disabled until a recursive role is picked — carrying the full
            // department list here would mount thousands of unreachable options.
            if (!system || !role?.recursive) {
                this.cachedDepartments = EMPTY_DEPARTMENTS;
                return EMPTY_DEPARTMENTS;
            }
            const occupied = this.occupancySlots();
            const keepId = this.selectedDepartment()?.id;
            const next = nsUserPermissionPolicy.filterDepartmentsForPicker(
                departments(),
                system,
                role,
                occupied,
                keepId,
            );
            this.cachedDepartments = nsUserPermissionPolicy.reuseArrayIfSameIds(
                this.cachedDepartments,
                next,
            );
            return this.cachedDepartments;
        });
        const roleComboboxEnable = ko.pureComputed(() => canInteract() && !!this.selectedSystem());
        // Legacy / PermissionRow: department only applies when the selected role is recursive.
        this.needsDepartment = ko.pureComputed(() => !!this.selectedRole()?.recursive);
        this.systemDescription = ko.pureComputed(() => {
            return String(this.selectedSystem()?.description ?? '').trim();
        });
        this.roleDescription = ko.pureComputed(() => {
            return String(this.selectedRole()?.description ?? '').trim();
        });

        this.systemComboboxParams = {
            data: this.availableSystems,
            selected: this.selectedSystem,
            enable: ko.pureComputed(() => canInteract()),
            lazyMountList: true,
            inputAttrs: {
                placeholder: ko.pureComputed(() => {
                    storeObservable('language')();
                    return lm.t('user.permissions.choose.system') ?? '';
                }),
            },
            option: {
                label: (system: ISystem) => system.name ?? '',
                html: (system: ISystem) => nsUserPermissions.comboboxOptionHtmlWithDescription(
                    system.name ?? '',
                    system.description,
                ),
                key: (system: ISystem) => system.id,
                tooltip: (system: ISystem) => {
                    storeObservable('language')();
                    return lm.t(nsTooltip.getTitle_system(system) ?? '') ?? '';
                },
            },
            title: (system: ISystem | null) => {
                storeObservable('language')();
                return system ? (lm.t(nsTooltip.getTitle_system(system) ?? '') ?? '') : '';
            },
            inputStatus: (system: ISystem | null) => system ? 'ok' : 'warning',
        };

        this.roleComboboxParams = {
            data: this.availableRoles,
            selected: this.selectedRole,
            enable: roleComboboxEnable,
            lazyMountList: true,
            reloadWhen: storeObservable('language'),
            // Occupancy can leave zero roles for the picked system — show empty chrome then.
            emptyPreparedListMessage: 'user.permissions.roles.noneavailable',
            inputAttrs: {
                placeholder: ko.pureComputed(() => {
                    storeObservable('language')();
                    return lm.t('user.permissions.choose.permission') ?? '';
                }),
            },
            option: {
                label: (role: ISystemRole) => this.getRoleDisplayName(role),
                html: (role: ISystemRole) => nsUserPermissions.comboboxOptionHtmlWithDescription(
                    this.getRoleDisplayName(role),
                    role.description,
                ),
                key: (role: ISystemRole) => role.id,
                tooltip: (role: ISystemRole) => {
                    storeObservable('language')();
                    const system = this.selectedSystem();
                    return system ? (lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? '') : '';
                },
                sortBy: [(role: ISystemRole) => {
                    if (this.selectedSystem()?.name == SYSTEM_MEGA_EADM) {
                        return role.roleenum;
                    }
                    return this.getRoleDisplayName(role).toLowerCase();
                }],
            },
            title: (role: ISystemRole | null) => {
                storeObservable('language')();
                const system = this.selectedSystem();
                if (!role || !system) {
                    return '';
                }
                return lm.t(nsTooltip.getTitle_role(system, role) ?? '') ?? '';
            },
            inputStatus: (role: ISystemRole | null) => {
                // No warning while system is empty/invalid — role is cleared and disabled.
                if (!this.selectedSystem()) {
                    return 'ok';
                }
                return role ? 'ok' : 'warning';
            },
        };

        this.departmentComboboxParams = {
            comboboxOptions: {
                data: this.availableDepartments,
                selected: this.selectedDepartment,
                enable: ko.pureComputed(() => canInteract() && this.needsDepartment()),
                // Department lists can be huge — keep the option DOM out until first open,
                // then render only the scrolled window.
                lazyMountList: true,
                virtualize: true,
                inputAttrs: {
                    placeholder: ko.pureComputed(() => {
                        storeObservable('language')();
                        return lm.t('user.permissions.choose.department') ?? '';
                    }),
                },
                option: {
                    key: (dept: TObjectData_basicDepartment) => dept.id,
                },
                inputStatus: (dept: TObjectData_basicDepartment | null) => {
                    // No warning while department is N/A (non-recursive) or still disabled.
                    if (!this.needsDepartment()) {
                        return 'ok';
                    }
                    return dept ? 'ok' : 'warning';
                },
            },
        };

        // Clear role/dept only when the system *id* changes — not when the combobox
        // rematches the same system to a different object reference (copy seed).
        this.kosub.subscribeChanged(this.selectedSystem, (next, prev) => {
            if ((next?.id ?? 0) == (prev?.id ?? 0)) {
                return;
            }
            this.selectedRole(null);
            this.selectedDepartment(null);
        });
        this.kosub.subscribe(this.selectedRole, role => {
            if (!role?.recursive) {
                this.selectedDepartment(null);
            }
        });
    }

    /** Prefill from a copied permission (system + optional role + expiry; last segment left blank by caller). */
    seedFrom(system: ISystem, role: ISystemRole | null, expirydate: string): void {
        this.selectedSystem(system);
        this.selectedRole(role);
        this.expirydate(expirydate ?? '');
    }

    dispose(): void {
        this.kosub.unsuscribeAll();
        this.needsDepartment.dispose();
        this.systemDescription.dispose();
        this.roleDescription.dispose();
        this.availableSystems.dispose();
        this.availableRoles.dispose();
        this.availableDepartments.dispose();
        this.occupancySlots.dispose();
    }

    /** True when every field on the row is blank. */
    isEmpty(): boolean {
        return !this.selectedSystem()
            && !this.selectedRole()
            && !this.selectedDepartment()
            && !this.expirydate()?.trim();
    }

    /** Required permission fields are filled (system + role + department when recursive). */
    isComplete(): boolean {
        if (!this.selectedSystem() || !this.selectedRole()) {
            return false;
        }
        if (this.needsDepartment() && !this.selectedDepartment()) {
            return false;
        }
        return true;
    }

    toPolicyIdentity(tempId: number): nsUserPermissionPolicy.IPermissionIdentity {
        return {
            id: tempId,
            systemId: this.selectedSystem()?.id ?? 0,
            roleId: this.selectedRole()?.id ?? 0,
            managerOf: this.selectedDepartment()?.id ?? 0,
            isEffectivelyRemoved: false,
        };
    }

    toManualPayload(fallbackCompanyId: string): ISystemPermission {
        const system = this.selectedSystem()!;
        const role = this.selectedRole()!;
        const department = this.selectedDepartment();
        return nsUserPermissions.buildManualSystemPermissionPayload({
            companyid: system.companyid ?? fallbackCompanyId,
            role,
            system,
            departmentId: department?.id ?? 0,
            departmentName: department?.name ?? '',
            expirydateInput: this.expirydate(),
            comment: '',
            id: 0,
        });
    }

    private getRoleDisplayName(role: ISystemRole): string {
        return getPermissionDisplayName(role, this.selectedSystem() ?? undefined) ?? role.name ?? '';
    }
}

/**
 * One organization accordion: header from getobject; searchsystem + departments lazy-loaded on expand.
 */
export class OrganizationPanel {
    organization: IOrganization;
    readonly isExpanded: KnockoutObservable<boolean> = ko.observable(false);
    readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    readonly isSystemsReady: KnockoutObservable<boolean> = ko.observable(false);
    /** Existing permissions grouped by system (A–Z), roles A–Z within each system. */
    readonly systemGroups: KnockoutObservableArray<SystemPermissionGroup> =
        ko.observableArray<SystemPermissionGroup>([]);
    /** System groups that still have at least one row under the page status filter. */
    readonly filteredSystemGroups: KnockoutComputed<SystemPermissionGroup[]>;
    /** Shared department list for all rows in this org (tuple searchobject). */
    readonly departments: KnockoutObservableArray<TObjectData_basicDepartment> =
        ko.observableArray<TObjectData_basicDepartment>([]);
    readonly permissionCount: KnockoutComputed<number>;
    /** Dim accordion + inert permissions while any page mutation is in flight. */
    readonly isDisabled: KnockoutComputed<boolean>;
    /** Freeze row/header selection checkboxes while busy or in bulk intermediate edit. */
    readonly isSelectionDisabled: KnockoutComputed<boolean>;
    /** Draft rows in the New Permission box (add-MP shell). */
    readonly newPermissionRows: KnockoutObservableArray<NewPermissionDraftRow> =
        ko.observableArray<NewPermissionDraftRow>([]);
    /**
     * True while drafts can be started/grown on this panel.
     * During page add-mode, only the active add panel (ephemeral or already drafting) may add rows.
     */
    readonly canAddDraftRows: KnockoutComputed<boolean>;
    /**
     * Add enabled when ≥1 complete draft exists, no partial drafts, and policy checks pass.
     */
    readonly canSubmitNewPermissions: KnockoutComputed<boolean>;
    /** Header pill while this org has draft adds or an in-progress row edit (status-v2 editing label). */
    readonly needsAttention: KnockoutComputed<boolean>;
    readonly attentionLabel: KnockoutComputed<string>;
    /** Shared page read-mode (no add / row-edit / bulk-edit). */
    readonly isReadMode: KnockoutObservable<boolean> | KnockoutComputed<boolean>;
    /** Stable id for scrollIntoView after Add Tenant. */
    readonly accordionDomId: string;
    /** Stable id for the New Permission draft box (copy / add scroll target). */
    readonly newPermissionBoxDomId: string;
    /**
     * Tenant added via Add eAdm Tenant and not yet persisted — cancel drafts removes the panel.
     */
    readonly isEphemeralTenant = ko.observable(false);
    /**
     * Bumped when org SP / override data changes so page-level status badges recompute
     * even when panels are still collapsed (no systemGroups yet).
     */
    readonly dataRevision = ko.observable(0);

    /** Cached searchsystem result — kept for later milestones. */
    systems: ISystem[] | null = null;

    private user: IObjectData;
    private readonly requestQueue: ConcurrentRequestQueue;
    /** Page-level syncfield ids for DEPARTMENT_SEARCH_COLS — not org-scoped. */
    private readonly departmentSyncfieldIds: string;
    private readonly rowActions: IPermissionRowActions;
    private readonly pageIsBusy: KnockoutObservable<boolean>;
    private readonly pageCanMutateDrafts: () => boolean;
    private readonly pageIsAddMode: () => boolean;
    private readonly statusFilter: KnockoutObservable<nsUserPermissions.TPermissionStatusFilter>;
    private readonly onDraftsCancelled: (() => void) | null;
    private readonly onSubmitNewPermissions: (() => void) | null;
    private readonly reqSearchSystem: PromiseRequest = global.getPromiseRequest('searchsystem');
    private readonly reqSearchDepartments = new PromiseRequest('searchobject');
    private readonly departmentIdKey = global.login.selectedOrganization()?.importformat?.id == ImportFormatId.Visma_HR8
        ? 'DepartmentNumber'
        : 'OrgUnitnr';
    private loadStarted = false;
    private expandPromise: Promise<void> | null = null;
    private cancelQueuedLoads: Array<() => void> = [];
    private assigneeRequests: PromiseRequest[] = [];
    private cancelAssigneeLoads: Array<() => void> = [];

    constructor(
        organization: IOrganization,
        user: IObjectData,
        requestQueue: ConcurrentRequestQueue,
        departmentSyncfieldIds: string,
        rowActions: IPermissionRowActions,
        pageIsBusy: KnockoutObservable<boolean>,
        pageCanMutateDrafts: () => boolean,
        pageIsAddMode: () => boolean,
        statusFilter: KnockoutObservable<nsUserPermissions.TPermissionStatusFilter>,
        onDraftsCancelled: (() => void) | null = null,
        onSubmitNewPermissions: (() => void) | null = null,
    ) {
        this.organization = organization;
        this.user = user;
        this.requestQueue = requestQueue;
        this.departmentSyncfieldIds = departmentSyncfieldIds;
        this.rowActions = rowActions;
        this.pageIsBusy = pageIsBusy;
        this.pageCanMutateDrafts = pageCanMutateDrafts;
        this.pageIsAddMode = pageIsAddMode;
        this.statusFilter = statusFilter;
        this.onDraftsCancelled = onDraftsCancelled;
        this.onSubmitNewPermissions = onSubmitNewPermissions;
        this.accordionDomId = `permissions-v2-org-${organization.organizationid}`;
        this.newPermissionBoxDomId = `${this.accordionDomId}-new-permission`;

        this.filteredSystemGroups = ko.pureComputed(() => {
            return this.systemGroups().filter(g => g.filteredPermissions().length > 0);
        });

        // Caret count = same rows the grid shows under the active filter (drafts excluded).
        this.permissionCount = ko.pureComputed(() => {
            if (this.systemGroups().length || this.isSystemsReady()) {
                return this.filteredSystemGroups().reduce(
                    (sum, g) => sum + g.filteredPermissions().length,
                    0,
                );
            }
            return this.countVisiblePermissionsMatchingFilter(this.statusFilter());
        });

        this.isDisabled = ko.pureComputed(() => this.pageIsBusy());
        this.isSelectionDisabled = ko.pureComputed(() => {
            return this.pageIsBusy() || !!this.rowActions.isBulkEditing();
        });
        this.canAddDraftRows = ko.pureComputed(() => {
            if (!this.pageCanMutateDrafts()) {
                return false;
            }
            if (!this.pageIsAddMode()) {
                return true;
            }
            return this.isEphemeralTenant() || this.newPermissionRows().length > 0;
        });
        this.canSubmitNewPermissions = ko.pureComputed(() => this.evaluateCanSubmitNewPermissions());
        this.needsAttention = ko.pureComputed(() => {
            if (this.newPermissionRows().length > 0) {
                return true;
            }
            const editing = this.rowActions.editingRow();
            if (!editing) {
                return false;
            }
            return this.systemGroups().some(g => g.permissions.includes(editing));
        });
        this.isReadMode = this.rowActions.isReadMode;
        this.attentionLabel = ko.pureComputed(() => {
            lm.selectedLanguage();
            return lm.t('user.permissions.org.status.editing') || 'Editing';
        });
    }

    toggle(): void {
        if (this.isDisabled()) {
            return;
        }
        if (this.isExpanded()) {
            this.isExpanded(false);
            return;
        }
        void this.expand();
    }

    /** Appends a draft row to the New Permission grid (shows the box on first click). */
    onAddPermissionClick(): void {
        if (!this.canAddDraftRows()) {
            return;
        }
        this.newPermissionRows.push(this.createNewPermissionDraftRow());
    }

    /** Copy into a new draft; leave last segment blank (department if recursive, else role). */
    copyPermissionFrom(row: PermissionRow): void {
        if (!this.canAddDraftRows()) {
            return;
        }
        const system = row.system;
        const role = row.role();
        if (!system || !role) {
            return;
        }
        const draft = this.createNewPermissionDraftRow();
        draft.seedFrom(
            system,
            row.needsDepartment() ? role : null,
            row.expirydate(),
        );
        this.newPermissionRows.push(draft);
        this.scrollNewPermissionBoxIntoView();
    }

    /** Scroll the New Permission box into view after KO has painted it. */
    scrollNewPermissionBoxIntoView(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                document.getElementById(this.newPermissionBoxDomId)
                    ?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
        });
    }

    /** Inserts one draft row if the New Permission box is empty (Add Tenant auto-insert). */
    ensureEmptyPermissionDraft(): void {
        if (this.newPermissionRows().length) {
            return;
        }
        this.newPermissionRows.push(this.createNewPermissionDraftRow());
    }

    private createNewPermissionDraftRow(): NewPermissionDraftRow {
        const draft: NewPermissionDraftRow = new NewPermissionDraftRow(
            () => this.systems ?? [],
            this.departments,
            () => this.getOccupancySlots({exceptDraft: draft}),
            this.organization,
            () => this.pageCanMutateDrafts(),
        );
        return draft;
    }

    /**
     * Active permission rows + other drafts as occupancy for combobox filtering.
     * Effectively-removed rows are omitted; `exceptDraft` / `exceptRow` skip the picker owner.
     */
    getOccupancySlots(opts?: {
        exceptDraft?: NewPermissionDraftRow;
        exceptRow?: PermissionRow;
    }): nsUserPermissionPolicy.IOccupancySlot[] {
        const slots: nsUserPermissionPolicy.IOccupancySlot[] = [];
        for (const group of this.systemGroups()) {
            for (const row of group.permissions) {
                if (row === opts?.exceptRow || row.isEffectivelyRemoved()) {
                    continue;
                }
                slots.push({
                    systemId: row.system?.id ?? row.systemPermission.system?.id ?? 0,
                    roleId: row.role()?.id ?? row.systemPermission.role?.id ?? 0,
                    managerOf: row.department()?.id ?? row.systemPermission.managerOf ?? 0,
                });
            }
        }
        for (const draft of this.newPermissionRows()) {
            if (draft === opts?.exceptDraft) {
                continue;
            }
            const system = draft.selectedSystem();
            if (!system) {
                continue;
            }
            slots.push({
                systemId: system.id,
                roleId: draft.selectedRole()?.id ?? 0,
                managerOf: draft.selectedDepartment()?.id ?? 0,
            });
        }
        return slots;
    }

    /** Discards all draft rows; ephemeral tenants notify the page to remove the accordion. */
    onCancelNewPermissionsClick(): void {
        this.discardNewPermissionDrafts();
        this.onDraftsCancelled?.();
    }

    /** Persists complete draft rows via the page (busy UI + updatepermission). */
    onSubmitNewPermissionsClick(): void {
        if (!this.canSubmitNewPermissions()) {
            return;
        }
        this.onSubmitNewPermissions?.();
    }

    /** Manual permission payloads for every complete non-empty draft row. */
    getCompleteDraftManualPermissions(): ISystemPermission[] {
        return this.newPermissionRows()
            .filter(row => !row.isEmpty() && row.isComplete())
            .map(row => row.toManualPayload(this.organization.organizationid));
    }

    discardNewPermissionDrafts(): void {
        this.newPermissionRows().forEach(row => row.dispose());
        this.newPermissionRows([]);
    }

    /** Empty rows ignored; need ≥1 complete row and no partial/policy-invalid rows. */
    private evaluateCanSubmitNewPermissions(): boolean {
        if (!this.pageCanMutateDrafts()) {
            return false;
        }
        const nonEmpty = this.newPermissionRows().filter(row => !row.isEmpty());
        if (!nonEmpty.length || nonEmpty.some(row => !row.isComplete())) {
            return false;
        }

        const existing = this.systemGroups()
            .flatMap(g => g.permissions)
            .filter(row => !row.isEffectivelyRemoved())
            .map(row => row.toPolicyIdentity());
        const draftIdentities = nonEmpty.map((row, index) => row.toPolicyIdentity(-(index + 1)));

        for (let i = 0; i < nonEmpty.length; i++) {
            const draft = draftIdentities[i]!;
            const siblings = [
                ...existing,
                ...draftIdentities.filter((_, j) => j !== i),
            ];
            const validation = nsUserPermissionPolicy.validatePermissionDraft(
                draft,
                siblings,
                nonEmpty[i]!.selectedSystem(),
            );
            if (!validation.allowed) {
                return false;
            }
        }
        return true;
    }

    /**
     * Opens the panel and loads searchsystem + departments once (concurrency-capped).
     * Resolves when systems are ready; rejects on load failure.
     */
    expand(): Promise<void> {
        this.isExpanded(true);
        if (this.isSystemsReady()) {
            return Promise.resolve();
        }
        if (this.expandPromise) {
            return this.expandPromise;
        }

        this.loadStarted = true;
        this.isSystemsReady(false);
        this.messageData({message: REQUEST_MESSAGE});

        const systemsJob = this.requestQueue.enqueue(() => {
            return this.reqSearchSystem.request(getSearchSystemParams({
                orgnr: this.organization.organizationid,
                resultsprpage: MAX_RESULTS_PER_PAGE,
                targetid: this.user.id,
            })) as Promise<ISystem[]>;
        });

        const departmentsJob = this.requestQueue.enqueue(() => {
            return this.reqSearchDepartments.request(getSearchObjectParams({
                objecttypeid: ObjectTypeId.Department,
                orgnr: this.organization.organizationid,
                onlytuples: true,
                resultsprpage: MAX_RESULTS_PER_PAGE,
                searchcols: DEPARTMENT_SEARCH_COLS.join(','),
                syncfieldids: this.departmentSyncfieldIds,
                sortcol: 'Department',
            })) as Promise<string[][]>;
        });

        this.cancelQueuedLoads = [systemsJob.cancel, departmentsJob.cancel];

        this.expandPromise = Promise.all([systemsJob.promise, departmentsJob.promise])
            .then(([systemsData, departmentTuples]) => {
                this.cancelQueuedLoads = [];
                this.systems = _(systemsData)
                    .filter((s: ISystem) => !!s.roles?.length)
                    .sortBy((s: ISystem) => s.name.toLowerCase())
                    .value();
                this.departments(this.mapDepartmentTuples(departmentTuples));
                this.reconcileSystemGroups();
                this.messageData(null);
                this.isSystemsReady(true);
                this.prefetchManualAssignedByNames();
            })
            .catch((reason: string) => {
                this.cancelQueuedLoads = [];
                this.abortAssigneePrefetch();
                this.loadStarted = false;
                this.isSystemsReady(false);
                this.messageData(createErrorMessage(String(reason)));
                throw reason;
            })
            .finally(() => {
                this.expandPromise = null;
            });

        return this.expandPromise;
    }

    /**
     * Apply refreshed getobject data for this org without collapsing or reloading systems/departments.
     * Reuses unchanged PermissionRow instances so unrelated rows keep assignee cache / selection.
     */
    applyUserData(user: IObjectData, organization: IOrganization): void {
        this.user = user;
        this.organization = organization;
        this.dataRevision(this.dataRevision() + 1);
        this.rebuildVisibleRows();
    }

    /** Drop one SP from this org and rebuild the grid when systems are loaded. */
    removeSystemPermissionById(permissionId: number): void {
        this.organization.systempermission = (this.organization.systempermission ?? [])
            .filter(sp => sp.id != permissionId);
        this.dataRevision(this.dataRevision() + 1);
        this.rebuildVisibleRows();
    }

    /** Replace one SP by id after a manual save, then reconcile rows. */
    replaceSystemPermission(updated: ISystemPermission): void {
        this.organization.systempermission = (this.organization.systempermission ?? [])
            .map(sp => sp.id == updated.id ? updated : sp);
        this.dataRevision(this.dataRevision() + 1);
        this.rebuildVisibleRows();
    }

    /** Reconcile rows after `user.overrides` changed (AP delete / restore) without getobject. */
    refreshRowsFromUser(user: IObjectData): void {
        this.user = user;
        this.dataRevision(this.dataRevision() + 1);
        this.rebuildVisibleRows();
    }

    /**
     * Permissions in this org matching a status filter (from SP + overrides — works collapsed).
     * Reads {@link dataRevision} so callers in pureComputeds stay reactive.
     */
    countMatchingFilter(filter: nsUserPermissions.TPermissionStatusFilter): number {
        this.dataRevision();
        return nsUserPermissions.countPermissionsMatchingStatusFilter(
            this.organization.systempermission ?? [],
            this.user.overrides ?? [],
            filter,
        );
    }

    /** Reconcile + assignee prefetch once systems/departments are ready. */
    private rebuildVisibleRows(): void {
        if (!this.isSystemsReady()) {
            return;
        }
        this.reconcileSystemGroups();
        this.prefetchManualAssignedByNames();
    }

    /** True when the grid would show at least one permission row (after AP/MP collapse). */
    hasVisiblePermissions(): boolean {
        return this.getVisibleSystemPermissions().length > 0;
    }

    private mapDepartmentTuples(tuples: string[][]): TObjectData_basicDepartment[] {
        const type = getObjectType(ObjectTypeId.Department);
        return _(tuples)
            .map((row: string[]) => {
                const obj = nsSyncFields.convertTuplesToObjectData(type, DEPARTMENT_SEARCH_COLS, row);
                const name = getObjectDisplayName(obj) ?? '';
                const identifier = getRawSyncFieldValue(this.departmentIdKey, obj.syncfields);
                return _.defaults({
                    name: identifier ? `${name} (${identifier})` : name,
                }, obj) as TObjectData_basicDepartment;
            })
            .sortBy((d: TObjectData_basicDepartment) => d.name.toLowerCase())
            .value();
    }

    /** Visible SPs after collapsing AP+active-R when a matching MP exists. */
    private getVisibleSystemPermissions(): ISystemPermission[] {
        return nsUserPermissions.filterVisibleSystemPermissions(
            this.organization.systempermission ?? [],
            this.user.overrides ?? [],
        );
    }

    /** Accordion count before rows exist: classify collapsed SPs with the same filter rules. */
    private countVisiblePermissionsMatchingFilter(
        filter: nsUserPermissions.TPermissionStatusFilter,
    ): number {
        return nsUserPermissions.countPermissionsMatchingStatusFilter(
            this.organization.systempermission ?? [],
            this.user.overrides ?? [],
            filter,
        );
    }

    /**
     * For SuperManagers: resolve each MP’s assignee via evaluateexpression (User, then ApiUser),
     * falling back to paired AP remove-override `overriddenby` when empty.
     * Skips rows that already completed a prefetch on this instance (reconcile reuse).
     */
    private prefetchManualAssignedByNames(): Promise<void> {
        this.abortAssigneePrefetch();
        if (!global.permissionValidator.isMinSuperManager()) {
            return Promise.resolve();
        }

        const allPermissions = this.organization.systempermission ?? [];
        const overrides = this.user.overrides ?? [];
        const manualRows = this.systemGroups()
            .flatMap(g => g.permissions)
            .filter(row => row.needsAssigneePrefetch() && row.systemPermission.role?.id);

        if (!manualRows.length) {
            return Promise.resolve();
        }

        const jobs = manualRows.map(row => {
            const roleId = row.systemPermission.role.id;
            // REFERENCE is per object type; integration-assigned MPs need ApiUser after User misses.
            const lookup = (objectTypeId: ObjectTypeId): Promise<string> => {
                const req = global.getPromiseRequest('evaluateexpression');
                this.assigneeRequests.push(req);
                const job = this.requestQueue.enqueue(() => {
                    return req.request({
                        objectid: this.user.id,
                        expression: nsUserPermissions.buildManualPermissionAssigneeExpression(roleId, objectTypeId),
                        orgnr: row.systemPermission.companyid,
                    }) as Promise<[string]>;
                });
                this.cancelAssigneeLoads.push(job.cancel);
                return job.promise
                    .then((str: [string]) => str[0])
                    .catch(() => '');
            };
            return lookup(ObjectTypeId.User)
                .then(name => name ? name : lookup(ObjectTypeId.ApiUser))
                .then((evaluatedName: string) => {
                    row.setAssignedByResolvedName(nsUserPermissions.resolveManualAssignedByDisplayName({
                        evaluatedName,
                        manual: row.systemPermission,
                        allPermissions,
                        overrides,
                    }));
                });
        });

        return Promise.all(jobs).then(() => {
            this.cancelAssigneeLoads = [];
        });
    }

    private abortAssigneePrefetch(): void {
        this.cancelAssigneeLoads.forEach(cancel => cancel());
        this.cancelAssigneeLoads = [];
        this.assigneeRequests.forEach(r => r.abort());
        this.assigneeRequests = [];
    }

    /**
     * Rebuild grouped rows from current org SPs, reusing PermissionRow instances when
     * SP identity still matches (avoids thrashing unrelated rows).
     */
    private reconcileSystemGroups(): void {
        const previousRows = this.systemGroups().flatMap(g => g.permissions);
        const previousByKey = new Map(previousRows.map(row => [
            permissionRowReconcileKey(row.systemPermission, row.getOwnOverride()),
            row,
        ]));

        // Drop group wrappers only — reused rows must stay alive.
        this.systemGroups().forEach(g => g.dispose({disposePermissions: false}));

        const sorted = _.sortBy(
            this.getVisibleSystemPermissions(),
            [
                (sp: ISystemPermission) => (sp.system?.name ?? '').toLowerCase(),
                (sp: ISystemPermission) => (sp.role?.name ?? '').toLowerCase(),
            ],
        );

        // Collect complete permission lists per system before constructing groups —
        // SystemPermissionGroup eagerly reads filteredPermissions; mid-build .push() would stale-cache.
        const pendingGroups: Array<{
            systemId: number;
            systemName: string;
            systemTooltip: string;
            systemHref: string | null;
            permissions: PermissionRow[];
        }> = [];
        const retained = new Set<PermissionRow>();
        const allPermissions = this.organization.systempermission ?? [];
        const overrides = this.user.overrides ?? [];
        const orgPolicy = nsUserPermissionPolicy.buildOrgContext(
            this.organization,
            this.systems,
            this.departments().map(d => d.id),
        );

        for (const sp of sorted) {
            const systemId = sp.system?.id ?? 0;
            const systemName = sp.system?.name ?? '';
            const system = this.systems?.find(s => s.id == systemId) ?? null;
            // Manual: only Altinn retain (K) as own override; R stays on paired AP.
            // Automatic: any matching A/R/K.
            const override = nsUserPermissions.findOwnOverrideForSystemPermission(sp, overrides);
            const pairedRemoveOverride = sp.manual
                ? nsUserPermissions.findPairedAutomaticRemoveOverride(sp, allPermissions, overrides)
                : null;

            const key = permissionRowReconcileKey(sp, override);
            const existing = previousByKey.get(key);
            let row: PermissionRow;
            if (existing && existing.matchesSpIdentity(sp)) {
                existing.applyOverrideState(sp, override, pairedRemoveOverride, orgPolicy);
                row = existing;
                retained.add(existing);
            } else {
                row = new PermissionRow(
                    sp,
                    system,
                    this.departments,
                    override,
                    this.rowActions,
                    orgPolicy,
                    pairedRemoveOverride,
                );
            }

            const last = pendingGroups[pendingGroups.length - 1];
            if (last && last.systemId === systemId && last.systemName === systemName) {
                last.permissions.push(row);
            } else {
                const tooltipSource = system ?? sp.system;
                pendingGroups.push({
                    systemId,
                    systemName,
                    systemTooltip: (tooltipSource ? nsTooltip.getTitle_system(tooltipSource) : null) ?? '',
                    systemHref: nsUserPermissions.systemAccessHref(tooltipSource),
                    permissions: [row],
                });
            }
        }

        for (const row of previousRows) {
            if (!retained.has(row)) {
                row.dispose();
            }
        }

        this.systemGroups(pendingGroups.map(g => new SystemPermissionGroup({
            ...g,
            statusFilter: this.statusFilter,
        })));
    }

    dispose(): void {
        this.cancelQueuedLoads.forEach(cancel => cancel());
        this.cancelQueuedLoads = [];
        this.abortAssigneePrefetch();
        this.systemGroups().forEach(group => group.dispose());
        this.newPermissionRows().forEach(row => row.dispose());
        this.newPermissionRows([]);
        this.filteredSystemGroups.dispose();
        this.permissionCount.dispose();
        this.isDisabled.dispose();
        this.isSelectionDisabled.dispose();
        this.canAddDraftRows.dispose();
        this.canSubmitNewPermissions.dispose();
        this.needsAttention.dispose();
        this.attentionLabel.dispose();
        this.reqSearchSystem.abort();
        this.reqSearchDepartments.abort();
    }
}
